import { useLocation } from 'react-router-dom'

import { FxIcon, FxSpinner } from '@/components/fx'
import { cn } from '@/lib/utils'
import { ApiAccessSection } from './settings/ApiAccessSection'
import { ArchiveSection } from './settings/ArchiveSection'
import { ArmCompareDialog } from './settings/ArmCompareDialog'
import { ConsentSection } from './settings/ConsentSection'
import { LlmCallLogDialog } from './settings/LlmCallLogDialog'
import { LogSection } from './settings/LogSection'
import { SchemaSection } from './settings/SchemaSection'
import { ThresholdSection } from './settings/ThresholdSection'
import { sectionDomId, sectionFromHash, SETTINGS_SECTIONS } from './settings/sections'
import { useCockpitContext, useCockpitDialog } from './useCockpit'

/**
 * `/templates/:templateId/einstellungen` — everything about one form that is
 * a setting rather than a document.
 *
 * ## What the gateway will and will not take
 *
 * `PATCH /v1/templates/:id` is bound to the gateway's own template struct
 * and then folded into a map by `UpdateTemplateDocument`
 * (`internal/templates/postgres.go`), which forwards a key **only when it is
 * non-empty**. Three keys are therefore accepted with a 200 and silently
 * dropped:
 *
 *   - `llmModel: ""`     — "back to the deployment default" does nothing
 *   - `recordSchema: null` — "back to the built-in schema" does nothing
 *   - `llmConsent: null`   — withdrawing consent does nothing
 *
 * (`confidenceThreshold` has the same shape but is only reachable in
 * 0.5…1.0 here, and `trainingAllowed` is a pointer, so `false` does arrive.)
 *
 * No control on this screen sends one of those three. Each is either not
 * offered, offered and disabled with the reason, or replaced by an
 * explanation of what really has to happen — because a settings screen that
 * reports "Gespeichert" over a value the server threw away is worse than one
 * that is missing a control.
 *
 * ## Layout
 *
 * A 260px side-nav of anchors beside a column of panels, as the artboard
 * draws it; below `md` the nav stacks above the sections. The nav's six
 * entries all lead somewhere — the artboard lists six and draws three, and a
 * nav item that scrolls to nothing is worse than no nav item.
 */
export function SettingsTab() {
  const cockpit = useCockpitContext()
  const location = useLocation()
  const { dialog, open, close } = useCockpitDialog()
  const active = sectionFromHash(location.hash)

  const template = cockpit.template
  if (!template) return <FxSpinner label="Einstellungen werden geladen …" />

  return (
    <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-[260px_minmax(0,860px)] md:gap-8">
      <nav aria-label="Einstellungen der Vorlage">
        <ul className="m-0 flex list-none gap-2 overflow-x-auto p-0 md:flex-col md:gap-3 md:overflow-visible">
          {SETTINGS_SECTIONS.map((section) => {
            const current = active === section.id
            return (
              <li key={section.id} className="shrink-0">
                <a
                  href={`#${sectionDomId(section.id)}`}
                  aria-current={current ? 'true' : undefined}
                  className={cn(
                    'flex h-control items-center gap-2.5 whitespace-nowrap rounded-sm px-3.5 text-body-strong no-underline md:w-full',
                    current
                      ? 'bg-primary-strong text-on-primary'
                      : 'bg-surface text-text hover:bg-primary-subtle hover:text-primary-text',
                  )}
                >
                  <FxIcon name={section.icon} size={20} />
                  {section.label}
                </a>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="flex min-w-0 flex-col gap-6">
        {/* `scroll-mt` keeps a jumped-to heading clear of the sticky chrome
            above it; `tabIndex={-1}` lets the browser move focus to the
            section an anchor names, which is the whole point of using one. */}
        <Section id="auswertung">
          <ThresholdSection stored={template.confidenceThreshold} canWrite={cockpit.canWrite} />
        </Section>

        <Section id="ki-zweitmeinung">
          <ConsentSection
            template={template}
            canWrite={cockpit.canWrite}
            canManage={cockpit.canManage}
            onOpenLog={() => open('protokoll')}
            onOpenArms={() => open('lesungen')}
          />
        </Section>

        <Section id="datensatz-schema">
          <SchemaSection template={template} canWrite={cockpit.canWrite} />
        </Section>

        <Section id="api-zugang">
          <ApiAccessSection templateId={cockpit.templateId} />
        </Section>

        <Section id="protokoll">
          <LogSection templateId={cockpit.templateId} onOpenLog={() => open('protokoll')} />
        </Section>

        <Section id="archivieren">
          <ArchiveSection
            templateId={cockpit.templateId}
            title={template.title}
            canWrite={cockpit.canWrite}
          />
        </Section>
      </div>

      {/* Both dialogs live in `?dialog=`, the cockpit's convention, so a
          protocol view is linkable and survives a reload. */}
      <LlmCallLogDialog
        templateId={cockpit.templateId}
        open={dialog === 'protokoll'}
        onClose={close}
      />
      <ArmCompareDialog open={dialog === 'lesungen'} onClose={close} />
    </div>
  )
}

function Section({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <div id={sectionDomId(id)} tabIndex={-1} className="scroll-mt-4 outline-none">
      {children}
    </div>
  )
}
